import { ChatPlannerSuggestion } from '@/services/aiPlanner';
import { ActivityType, EnhancedSuggestion } from '@/components/suggestions/EnhancedSuggestionCard';
import type { ItineraryItem } from '@/services/itinerary';
import type { TripDestination } from '@/services/trips';

/**
 * Infers activity type from suggestion title and description
 * Uses keyword matching to categorize activities
 */
export function inferActivityType(
  title: string | undefined,
  description: string | undefined,
): ActivityType | undefined {
  const text = `${title ?? ''} ${description ?? ''}`.toLowerCase();

  // Food & Dining
  if (
    /\b(restaurant|cafe|bar|food|dining|eat|meal|cuisine|culinary|gastronomy|market|food tour|wine|beer|cocktail|brunch|lunch|dinner|street food)\b/.test(
      text
    )
  ) {
    return 'food';
  }

  // Culture & Museums
  if (
    /\b(museum|gallery|art|exhibition|culture|cultural|theater|theatre|opera|ballet|concert|symphony)\b/.test(
      text
    )
  ) {
    return 'culture';
  }

  // Adventure
  if (
    /\b(adventure|hiking|climbing|biking|cycling|kayaking|rafting|surfing|skydiving|bungee|extreme|sports|trekking)\b/.test(
      text
    )
  ) {
    return 'adventure';
  }

  // Nature & Outdoors
  if (
    /\b(park|nature|wildlife|forest|mountain|beach|ocean|sea|lake|river|garden|botanical|hiking trail|nature reserve|national park)\b/.test(
      text
    )
  ) {
    return 'nature';
  }

  // Nightlife
  if (
    /\b(nightlife|night club|club|disco|party|nightlife|evening|night|bar|pub|live music|dj|dance)\b/.test(
      text
    ) &&
    !/\b(restaurant|cafe)\b/.test(text)
  ) {
    return 'nightlife';
  }

  // Shopping
  if (
    /\b(shopping|market|mall|boutique|store|shop|souvenir|bazaar|shopping district|retail)\b/.test(
      text
    )
  ) {
    return 'shopping';
  }

  // Relaxation
  if (
    /\b(spa|relax|massage|wellness|yoga|meditation|beach|pool|resort|retreat|thermal|hot spring)\b/.test(
      text
    )
  ) {
    return 'relaxation';
  }

  // Historical
  if (
    /\b(historic|historical|monument|palace|castle|cathedral|church|temple|ruins|archaeological|heritage|ancient|old town|medieval)\b/.test(
      text
    )
  ) {
    return 'historical';
  }

  return undefined;
}

const normalizeCategory = (category?: string): string | undefined =>
  typeof category === 'string' ? category.trim().toLowerCase() : undefined;

export function mapCategoryToActivityType(category?: string): ActivityType | undefined {
  const normalized = normalizeCategory(category);
  if (!normalized) return undefined;
  if (/(food|cuisine|dining|restaurant|culinary|market)/.test(normalized)) return 'food';
  if (/(museum|culture|cultural|gallery|art|exhibition)/.test(normalized)) return 'culture';
  if (/(landmark|historic|historical|monument|heritage|cathedral|temple|castle|palace)/.test(normalized)) return 'historical';
  if (/(nature|outdoor|park|garden|beach|hike|trail|wildlife)/.test(normalized)) return 'nature';
  if (/(adventure|tour|excursion|extreme|sport|biking|hiking)/.test(normalized)) return 'adventure';
  if (/(nightlife|bar|club|music|evening)/.test(normalized)) return 'nightlife';
  if (/(shopping|market|mall|boutique)/.test(normalized)) return 'shopping';
  if (/(relax|wellness|spa|retreat|resort)/.test(normalized)) return 'relaxation';
  if (/(package|bundle|pass)/.test(normalized)) return undefined;
  return undefined;
}

export function formatCategoryLabel(category?: string): string | undefined {
  const normalized = normalizeCategory(category);
  if (!normalized) return undefined;
  const labelMap: Record<string, string> = {
    cuisine: 'Cuisine',
    food: 'Cuisine',
    dining: 'Cuisine',
    restaurant: 'Cuisine',
    landmark: 'Landmark',
    landmarks: 'Landmark',
    museum: 'Museum',
    museums: 'Museum',
    culture: 'Culture',
    cultural: 'Culture',
    nature: 'Nature',
    outdoors: 'Outdoors',
    shopping: 'Shopping',
    nightlife: 'Nightlife',
    adventure: 'Adventure',
    trendy: 'Trendy',
    package: 'Package',
    pass: 'Package',
    bundle: 'Package',
  };
  if (labelMap[normalized]) return labelMap[normalized];
  return normalized
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
    .join(' ');
}

/**
 * Normalizes a string for comparison (lowercase, trim, remove special chars)
 */
function normalizeString(str: string | undefined | null): string {
  const s = typeof str === 'string' ? str : '';
  return s
    .toLowerCase()
    .trim()
    .replace(/[^\w\s]/g, '') // Remove special characters
    .replace(/\s+/g, ' '); // Normalize whitespace
}

/**
 * Calculates similarity between two strings (0-1, where 1 is identical)
 * Uses a simple word-based similarity algorithm
 */
function calculateSimilarity(str1: string, str2: string): number {
  const normalized1 = normalizeString(str1);
  const normalized2 = normalizeString(str2);
  
  // Exact match
  if (normalized1 === normalized2) {
    return 1.0;
  }
  
  // Check if one contains the other (high similarity)
  if (normalized1.includes(normalized2) || normalized2.includes(normalized1)) {
    const longer = normalized1.length > normalized2.length ? normalized1 : normalized2;
    const shorter = normalized1.length > normalized2.length ? normalized2 : normalized1;
    return shorter.length / longer.length;
  }
  
  // Word-based similarity
  const words1 = new Set(normalized1.split(' ').filter(w => w.length > 2));
  const words2 = new Set(normalized2.split(' ').filter(w => w.length > 2));
  
  if (words1.size === 0 || words2.size === 0) {
    return 0;
  }
  
  const intersection = new Set([...words1].filter(w => words2.has(w)));
  const union = new Set([...words1, ...words2]);
  
  return intersection.size / union.size;
}

/**
 * Checks if a suggestion matches an existing itinerary item
 * Returns true if the suggestion is similar to an existing item (duplicate)
 */
export function isSuggestionDuplicate(
  suggestion: ChatPlannerSuggestion,
  existingItems: ItineraryItem[] | undefined | null,
  similarityThreshold: number = 0.7
): boolean {
  const suggestionTitle = normalizeString(suggestion.title);
  const items = Array.isArray(existingItems) ? existingItems : [];

  for (const item of items) {
    const itemTitle = normalizeString(item.title);
    
    // Check title similarity
    const titleSimilarity = calculateSimilarity(suggestionTitle, itemTitle);
    if (titleSimilarity >= similarityThreshold) {
      return true;
    }
    
    // Also check if the suggestion address matches the item address (if both have addresses)
    if (suggestion.address && item.address) {
      const suggestionAddress = normalizeString(suggestion.address);
      const itemAddress = normalizeString(item.address);
      if (suggestionAddress === itemAddress) {
        return true;
      }
    }
  }
  
  return false;
}

/**
 * Filters out suggestions that are duplicates of existing itinerary items
 */
export function filterDuplicateSuggestions(
  suggestions: ChatPlannerSuggestion[] | undefined | null,
  existingItems: ItineraryItem[] | undefined | null,
): ChatPlannerSuggestion[] {
  const list = Array.isArray(suggestions) ? suggestions : [];
  return list.filter((suggestion) => !isSuggestionDuplicate(suggestion, existingItems));
}

const deriveImageLocationFromAddress = (address?: string): string | undefined => {
  if (!address) return undefined;
  const parts = address
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  if (!parts.length) return undefined;

  for (let i = parts.length - 1; i >= 0; i -= 1) {
    const segment = parts[i]!;
    // Skip postal codes / mixed numeric fragments.
    if (/\d/.test(segment)) continue;
    if (segment.length < 2) continue;
    return segment;
  }
  return undefined;
};

/**
 * Maps ChatPlannerSuggestion to EnhancedSuggestion format
 */
export function mapToEnhancedSuggestion(
  suggestion: ChatPlannerSuggestion,
  destination?: string
): EnhancedSuggestion {
  const activityType =
    mapCategoryToActivityType(suggestion.category) ||
    inferActivityType(suggestion.title, suggestion.description);
  const categoryLabel = formatCategoryLabel(suggestion.category);

  const desc = suggestion.description ?? '';
  const locationMatch = desc.match(
    /(?:in|at|near|visit|explore)\s+([A-Z][a-zA-Z\s]+?)(?:\.|,|$|and)/i
  );
  const parsedFromDescription = locationMatch ? locationMatch[1].trim() : undefined;
  const parsedFromAddress = deriveImageLocationFromAddress(suggestion.address);
  const imageLocation =
    suggestion.city?.trim() ||
    parsedFromAddress ||
    parsedFromDescription ||
    destination;

  return {
    id: `${suggestion.day ?? 1}-${suggestion.title ?? 'idea'}`,
    title: suggestion.title ?? '',
    description: suggestion.description ?? '',
    day: suggestion.day,
    activityType,
    categoryLabel,
    location: imageLocation,
    address: suggestion.address, // Store address separately
    estimatedCost: suggestion.cost,
    currency: suggestion.currency,
    ticketLink: suggestion.ticketLink,
    needsReservation: suggestion.needsTicket,
    duration: undefined,
    bestTime: undefined,
    weatherDependent: undefined,
    confidence: undefined,
    imageUrl: undefined,
    alternatives: undefined,
    budgetBreakdown: undefined,
  };
}

export type AiCitySection = {
  key: string;
  label: string | null;
  items: ChatPlannerSuggestion[];
};

/**
 * Group AI plan cards by `city` when the model returns multiple cities or mixed with unknowns.
 */
export function buildAiCitySections(
  suggestions: ChatPlannerSuggestion[] | undefined | null,
  destinations?: TripDestination[] | undefined,
): { shouldGroup: boolean; sections: AiCitySection[] } {
  const list = Array.isArray(suggestions) ? suggestions : [];
  if (!list.length) {
    return { shouldGroup: false, sections: [] };
  }

  const tripCities = (destinations ?? [])
    .map((d) => d?.city?.trim())
    .filter((c): c is string => Boolean(c));

  type Bucket = { display: string; items: ChatPlannerSuggestion[] };
  const byKey = new Map<string, Bucket>();
  const ungrouped: ChatPlannerSuggestion[] = [];

  for (const s of list) {
    const raw =
      typeof s.city === 'string' && s.city.trim()
        ? s.city.trim().split(',')[0].trim()
        : '';
    if (!raw) {
      ungrouped.push(s);
      continue;
    }
    const key = raw.toLowerCase();
    const prev = byKey.get(key);
    if (prev) {
      prev.items.push(s);
    } else {
      byKey.set(key, { display: raw, items: [s] });
    }
  }

  const distinctKeys = [...byKey.keys()];

  if (distinctKeys.length === 0) {
    return {
      shouldGroup: false,
      sections: [{ key: 'all', label: null, items: [...list] }],
    };
  }

  const needsSections = distinctKeys.length >= 2 || ungrouped.length > 0;

  if (!needsSections) {
    return {
      shouldGroup: false,
      sections: [{ key: 'all', label: null, items: [...list] }],
    };
  }

  const order: string[] = [];
  for (const tc of tripCities) {
    const k = tc.toLowerCase();
    if (byKey.has(k) && !order.includes(k)) order.push(k);
  }
  for (const k of distinctKeys.sort()) {
    if (!order.includes(k)) order.push(k);
  }

  const sections: AiCitySection[] = order.map((k) => {
    const b = byKey.get(k)!;
    return { key: k, label: b.display, items: b.items };
  });

  if (ungrouped.length) {
    sections.push({ key: 'other', label: 'Other', items: ungrouped });
  }

  return { shouldGroup: true, sections };
}

